import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import {
  LifeBuoy,
  Mail,
  ExternalLink,
  BookOpen,
  ClipboardList,
  Copy,
  Clock3,
  ShieldCheck,
} from 'lucide-react'

function buildDiagnosticText({ user, organizationName }) {
  const location = window.location
  const lines = [
    'Xtract Support Diagnostic Context',
    '--------------------------------',
    `Organisation: ${organizationName || 'Unknown'}`,
    `User: ${user?.name || user?.email || 'Unknown'}`,
    `Role: ${user?.role || 'Unknown'}`,
    `URL: ${location.href}`,
    `Host: ${location.host}`,
    `Timestamp: ${new Date().toISOString()}`,
  ]
  return lines.join('\n')
}

export default function ContactSupport() {
  const { user, organizationName } = useAuth()
  const toast = useToast()

  const supportSubject = useMemo(() => {
    const org = organizationName || 'Organisation'
    return encodeURIComponent(`[Xtract Support] ${org} - Help Request`)
  }, [organizationName])

  const supportBody = useMemo(() => {
    const body = [
      'Hi Support Team,',
      '',
      'I need help with:',
      '- Issue summary:',
      '- Impact:',
      '- Steps already tried:',
      '',
      'Diagnostic context:',
      buildDiagnosticText({ user, organizationName }),
    ].join('\n')

    return encodeURIComponent(body)
  }, [organizationName, user])

  const copyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(buildDiagnosticText({ user, organizationName }))
      toast.success('Diagnostics copied', 'Support context has been copied to your clipboard.')
    } catch (err) {
      console.error('Failed to copy diagnostics', err)
      toast.error('Copy failed', 'Please copy the details manually from this page.')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10">
            <LifeBuoy className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Contact & Support</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Get help quickly with the right support channel and diagnostic details.
            </p>
          </div>
        </div>
        <button
          onClick={copyDiagnostics}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3.5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <Copy className="h-4 w-4" />
          Copy Diagnostics
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <a
          href={`mailto:info@synapx.com?subject=${supportSubject}&body=${supportBody}`}
          className="group rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="h-10 w-10 rounded-lg bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300 flex items-center justify-center">
              <Mail className="h-5 w-5" />
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400" />
          </div>
          <h2 className="mt-4 text-base font-semibold text-gray-900 dark:text-gray-100">Technical Support</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">info@synapx.com</p>
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Use this for extraction issues, connector failures, and workflow errors.</p>
        </a>

        <a
          href="mailto:info@synapx.com"
          className="group rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="h-10 w-10 rounded-lg bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400" />
          </div>
          <h2 className="mt-4 text-base font-semibold text-gray-900 dark:text-gray-100">Account & Commercial</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">info@synapx.com</p>
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Use this for onboarding, account updates, licensing, and deployment planning.</p>
        </a>

        <Link
          to="/guide"
          className="group rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="h-10 w-10 rounded-lg bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300 flex items-center justify-center">
              <BookOpen className="h-5 w-5" />
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400" />
          </div>
          <h2 className="mt-4 text-base font-semibold text-gray-900 dark:text-gray-100">How-To Guide</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Self-service documentation</p>
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Browse guided walkthroughs before raising a ticket to reduce turnaround time.</p>
        </Link>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList className="h-4.5 w-4.5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">What To Include In A Support Request</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/40 px-3 py-2.5 text-gray-700 dark:text-gray-300">A short summary of the issue and when it started</div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/40 px-3 py-2.5 text-gray-700 dark:text-gray-300">Any request IDs, email IDs, or template names involved</div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/40 px-3 py-2.5 text-gray-700 dark:text-gray-300">Expected behavior versus current behavior</div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/40 px-3 py-2.5 text-gray-700 dark:text-gray-300">Screenshots and browser timestamp for failed actions</div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Support SLA Guide</h2>
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-gray-50/70 dark:bg-gray-800/40">
              <span className="text-sm text-gray-600 dark:text-gray-300">Critical outage</span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 dark:text-red-300"><Clock3 className="h-3.5 w-3.5" /> &lt; 4h</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-gray-50/70 dark:bg-gray-800/40">
              <span className="text-sm text-gray-600 dark:text-gray-300">Workflow blocked</span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-300"><Clock3 className="h-3.5 w-3.5" /> same day</span>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-gray-50/70 dark:bg-gray-800/40">
              <span className="text-sm text-gray-600 dark:text-gray-300">General queries</span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-navy-700 dark:text-navy-300"><Clock3 className="h-3.5 w-3.5" /> 1-2 days</span>
            </div>
          </div>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Response windows are indicative and may vary by environment complexity.</p>
        </div>
      </div>
    </div>
  )
}
